import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { store } from './store';
import { AppDispatch, RootState } from './store';
import theme from './theme';
import { loadUser } from './store/slices/authSlice';
import { ENABLE_PUBLIC_REGISTRATION } from './config/features';

// Components
import Navbar from './components/Layout/Navbar';
import PrivateRoute from './components/Layout/PrivateRoute';
import MobileUploadProtectedRoute from './components/Layout/MobileUploadProtectedRoute';
import DeviceAccessProtectedRoute from './components/Layout/DeviceAccessProtectedRoute';
import KeepAliveOutlet from './components/Layout/KeepAliveOutlet';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import Dashboard from './components/Dashboard';
import UserAiThemes from './components/User/UserAiThemes';
import Photos from './components/Photos';
import Videos from './components/Videos';
import Profile from './components/Profile';
import Collections from './components/User/Collections';
import Unauthorized from './components/Unauthorized';
import UserManagement from './components/Admin/UserManagement';
import StaffManagement from './components/Admin/StaffManagement';
import UserDetail from './components/Admin/UserDetail';
import UserCustomFrameAssets from './components/Admin/UserCustomFrameAssets';
import PhotosHub from './components/User/PhotosHub';
import AdminPhotos from './components/Admin/Photos';
import UserVideos from './components/User/Videos';
import AdminVideos from './components/Admin/Videos';
import DesktopList from './components/Admin/DesktopList';
import AdminDesktopMachineDetail from './components/Admin/AdminDesktopMachineDetail';
import CustomFrameDetail from './components/User/CustomFrameDetail';
import CustomFramePage from './components/CustomFrame';
import PublicAlbumPages from './components/Admin/PublicAlbumPages';
import AdminLandingPage from './components/Admin/AdminLandingPage';
import EmailVerification from './components/Auth/EmailVerification';
import ForgotPassword from './components/Auth/ForgotPassword';
import LivePhotos from './components/Photos/LivePhotos';
import ThemeDetail from './components/Theme/ThemeDetail';
import ThemeFrames from './components/Theme/ThemeFrames';
import EventManagement from './components/EventManagement';
import CustomerInformation from './components/CustomerInformation';
import SoftwareUpdate from './components/Admin/SoftwareUpdate';
import EmailComposer from './components/Admin/EmailComposer';
import EmailSent from './components/Admin/EmailSent';
import BackgroundAi from './components/Admin/BackgroundAi';
import Software from './components/User/Software';
import MobileUploadMachineList from './components/User/MobileUploadMachineList';
import MobileUploadMachineDetail from './components/User/MobileUploadMachineDetail';
import MPhotoMono from './components/User/MPhotoMono';
import MPhotoLite from './components/User/MPhotoLite';
import UserStaff from './components/User/UserStaff';
import PaymentSePay from './components/PaymentPayOs';
import {
  Container,
  Paper,
  Typography,
  Alert,
  Box,
} from '@mui/material';
import { Lock } from '@mui/icons-material';

// Wrapper components để sử dụng useParams đúng cách
const UserDetailWrapper = () => {
  return <UserDetail />;
};

const ProThemeDetailRoute = () => <ThemeDetail channel="pro" />;
const ProThemeFramesRoute = () => <ThemeFrames channel="pro" />;
const LiteThemeDetailRoute = () => <ThemeDetail channel="lite" />;
const LiteThemeFramesRoute = () => <ThemeFrames channel="lite" />;

const AdminProThemeDetailRoute = () => {
  const { userId } = useParams<{ userId: string }>();
  return <ThemeDetail channel="pro" userId={userId} />;
};
const AdminProThemeFramesRoute = () => {
  const { userId } = useParams<{ userId: string }>();
  return <ThemeFrames channel="pro" userId={userId} />;
};
const AdminLiteThemeDetailRoute = () => {
  const { userId } = useParams<{ userId: string }>();
  return <ThemeDetail channel="lite" userId={userId} />;
};
const AdminLiteThemeFramesRoute = () => {
  const { userId } = useParams<{ userId: string }>();
  return <ThemeFrames channel="lite" userId={userId} />;
};

const LegacyThemeDetailRedirect = () => {
  const { themeId } = useParams<{ themeId: string }>();
  return <Navigate to={`/m-photo-pro/themes/${themeId}`} replace />;
};

const LegacyThemeFramesRedirect = () => {
  const { themeId, customFrameId } = useParams<{ themeId: string; customFrameId: string }>();
  return (
    <Navigate
      to={`/m-photo-pro/themes/${themeId}/custom-frames/${customFrameId}`}
      replace
    />
  );
};

/** Guard: user phải có sePayEnabled=true mới vào được trang SePay */
const SePayProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  if (user && user.sePayEnabled === false) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Box sx={{ mb: 2 }}><Lock sx={{ fontSize: 56, color: 'text.disabled' }} /></Box>
          <Typography variant="h6" gutterBottom>Tính năng chưa được kích hoạt</Typography>
          <Alert severity="info" sx={{ textAlign: 'left', mt: 1 }}>
            Thanh toán SePay chưa được bật cho tài khoản của bạn. Vui lòng liên hệ admin để được cấp quyền.
          </Alert>
        </Paper>
      </Container>
    );
  }
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, isLoading, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [initialAuthChecked, setInitialAuthChecked] = React.useState(false);

  // Kiểm tra xác thực khi component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await dispatch(loadUser()).unwrap();
      } catch {
        /* chưa đăng nhập */
      } finally {
        setInitialAuthChecked(true);
      }
    };

    checkAuth();
  }, [dispatch]);

  // Hiển thị loading khi đang kiểm tra xác thực ban đầu
  if (!initialAuthChecked) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div className="loading-spinner"></div>
        <div>Đang tải...</div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <Routes>
        <Route 
          path="/login" 
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <Login />
          } 
        />
        <Route
          path="/register"
          element={
            !ENABLE_PUBLIC_REGISTRATION ? (
              <Navigate to="/login" replace />
            ) : isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <Register />
            )
          }
        />
        <Route path="/unauthorized" element={<Unauthorized />} />

        <Route element={<KeepAliveOutlet />}>
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/ai-themes"
          element={
            <PrivateRoute>
              <UserAiThemes />
            </PrivateRoute>
          }
        />
        <Route
          path="/m-photo-pro"
          element={
            <PrivateRoute>
              <PhotosHub />
            </PrivateRoute>
          }
        />
        <Route path="/photos" element={<Navigate to="/m-photo-pro" replace />} />

        <Route
          path="/videos"
          element={
            <PrivateRoute>
              <UserVideos />
            </PrivateRoute>
          }
        />

        <Route
          path="/icons"
          element={
            <PrivateRoute requiredRole="USER">
              <DeviceAccessProtectedRoute required="lite">
                <Navigate to="/m-photo-lite?tab=icons" replace />
              </DeviceAccessProtectedRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          }
        />

        <Route
          path="/payment-payos"
          element={
            <PrivateRoute requiredRole="USER">
              <SePayProtectedRoute>
                <PaymentSePay />
              </SePayProtectedRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/events"
          element={
            <PrivateRoute>
              <EventManagement />
            </PrivateRoute>
          }
        />

        <Route
          path="/custom-frames"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <CustomFramePage />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/landing-page"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <AdminLandingPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/public-custom-frame-menu"
          element={<Navigate to="/admin/public-albums" replace />}
        />
        <Route
          path="/admin/public-albums"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <PublicAlbumPages />
            </PrivateRoute>
          }
        />

        <Route
          path="/users"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserManagement />
            </PrivateRoute>
          }
        />

        <Route
          path="/staff"
          element={
            <PrivateRoute requiredRole="ADMIN">
              <StaffManagement />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/desktops"
          element={
            <PrivateRoute requiredRole="ADMIN">
              <DesktopList />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/desktops/:machineCode"
          element={
            <PrivateRoute requiredRole="ADMIN">
              <AdminDesktopMachineDetail />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserDetailWrapper />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/photos"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserDetailWrapper />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/m-photo-pro"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserDetailWrapper />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/videos"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserDetailWrapper />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/icons"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserDetailWrapper />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/mobile-upload"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserDetailWrapper />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/device"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserDetailWrapper />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/m-photo-mono"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserDetailWrapper />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/m-photo-lite"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserDetailWrapper />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/staff"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserDetailWrapper />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/sepay"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserDetailWrapper />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/collections"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserDetailWrapper />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/custom-frames"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserDetailWrapper />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/custom-frames/:customFrameId"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserCustomFrameAssets />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/m-photo-lite/custom-frames/:customFrameId"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserCustomFrameAssets channel="lite" />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/media"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <UserDetailWrapper />
            </PrivateRoute>
          }
        />

        <Route path="/verify-email" element={<EmailVerification />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        <Route path="/live-photos" element={<LivePhotos />} />

        <Route
          path="/m-photo-pro/themes/:themeId/custom-frames/:customFrameId"
          element={
            <PrivateRoute requiredRole="USER">
              <ProThemeFramesRoute />
            </PrivateRoute>
          }
        />

        <Route
          path="/m-photo-pro/themes/:themeId"
          element={
            <PrivateRoute requiredRole="USER">
              <ProThemeDetailRoute />
            </PrivateRoute>
          }
        />

        <Route
          path="/m-photo-lite/themes/:themeId/custom-frames/:customFrameId"
          element={
            <PrivateRoute requiredRole="USER">
              <DeviceAccessProtectedRoute required="lite">
                <LiteThemeFramesRoute />
              </DeviceAccessProtectedRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/m-photo-lite/themes/:themeId"
          element={
            <PrivateRoute requiredRole="USER">
              <DeviceAccessProtectedRoute required="lite">
                <LiteThemeDetailRoute />
              </DeviceAccessProtectedRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/m-photo-pro/themes/:themeId/custom-frames/:customFrameId"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <AdminProThemeFramesRoute />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/m-photo-pro/themes/:themeId"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <AdminProThemeDetailRoute />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/m-photo-lite/themes/:themeId/custom-frames/:customFrameId"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <AdminLiteThemeFramesRoute />
            </PrivateRoute>
          }
        />

        <Route
          path="/users/:userId/m-photo-lite/themes/:themeId"
          element={
            <PrivateRoute requiredRole={['ADMIN', 'STAFF'] as string[]}>
              <AdminLiteThemeDetailRoute />
            </PrivateRoute>
          }
        />

        {/* Legacy theme routes → M-Photo Pro */}
        <Route path="/themes" element={<Navigate to="/m-photo-pro?proTab=themes" replace />} />
        <Route path="/themes/:themeId" element={<LegacyThemeDetailRedirect />} />
        <Route
          path="/themes/:themeId/custom-frames/:customFrameId"
          element={<LegacyThemeFramesRedirect />}
        />

        <Route
          path="/user/profile"
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          }
        />

        <Route
          path="/user/photos"
          element={
            <PrivateRoute>
              <PhotosHub />
            </PrivateRoute>
          }
        />

        <Route
          path="/user/collections"
          element={
            <PrivateRoute>
              <Collections />
            </PrivateRoute>
          }
        />

        <Route
          path="/user/custom-frames"
          element={
            <PrivateRoute>
              <DeviceAccessProtectedRoute required="pro">
                <Navigate to="/m-photo-pro?proTab=custom-frames" replace />
              </DeviceAccessProtectedRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/user/custom-frames/:customFrameId"
          element={
            <PrivateRoute>
              <DeviceAccessProtectedRoute required="pro">
                <CustomFrameDetail channel="pro" />
              </DeviceAccessProtectedRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/m-photo-lite/custom-frames/:customFrameId"
          element={
            <PrivateRoute requiredRole="USER">
              <DeviceAccessProtectedRoute required="lite">
                <CustomFrameDetail channel="lite" />
              </DeviceAccessProtectedRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/user/media"
          element={
            <PrivateRoute>
              <UserVideos />
            </PrivateRoute>
          }
        />

        <Route
          path="/customer-information"
          element={
            <PrivateRoute>
              <CustomerInformation />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/software-update"
          element={
            <PrivateRoute requiredRole="ADMIN">
              <SoftwareUpdate />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/email-composer"
          element={
            <PrivateRoute requiredRole="ADMIN">
              <EmailComposer />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/background-ai"
          element={
            <PrivateRoute requiredRole="ADMIN">
              <BackgroundAi />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/email-sent"
          element={
            <PrivateRoute requiredRole="ADMIN">
              <EmailSent />
            </PrivateRoute>
          }
        />

        <Route
          path="/software"
          element={
            <PrivateRoute>
              <DeviceAccessProtectedRoute required="any-software">
                <Software />
              </DeviceAccessProtectedRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/mobile-upload"
          element={
            <PrivateRoute>
              <MobileUploadProtectedRoute>
                <MobileUploadMachineList />
              </MobileUploadProtectedRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/staff-users"
          element={
            <PrivateRoute requiredRole="USER">
              <UserStaff />
            </PrivateRoute>
          }
        />

        <Route
          path="/m-photo-mono"
          element={
            <PrivateRoute requiredRole={['USER', 'STAFF_USER'] as string[]}>
              <DeviceAccessProtectedRoute required="mono">
                <MPhotoMono />
              </DeviceAccessProtectedRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/m-photo-lite"
          element={
            <PrivateRoute requiredRole={['USER', 'STAFF_USER'] as string[]}>
              <DeviceAccessProtectedRoute required="lite">
                <MPhotoLite />
              </DeviceAccessProtectedRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/mobile-upload/:machineCode"
          element={
            <PrivateRoute>
              <MobileUploadProtectedRoute>
                <MobileUploadMachineDetail />
              </MobileUploadProtectedRoute>
            </PrivateRoute>
          }
        />

        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastContainer />
    </>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </Provider>
  );
};

export default App;
